import onboardingApi from 'api/onboarding/onboarding.api';
import { LAST_SEEN_ONBOARDING_STEP, MEMORY_TOUR_COMPLETED, MEMORY_TOUR_STEP } from 'constants/user';
import { saveAccountProperties } from 'redux/modules/accounts.actions';
import type { OnboardingFormData } from 'redux/modules/accounts.types';
import { AcceptedAccountOnboardingProperties } from 'redux/modules/accounts.types';
import { clearTour, saveUser, saveUserProperty } from 'redux/modules/user.actions';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule, xhrStatusFactory } from 'redux/reusable/xhrStatus';
import { getIsTourForced, getUser } from 'redux/selectors/user';
import type { Account } from 'types/account';
import type { Thunk } from 'types/redux';
import { actionCreatorFactory } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import debug from 'utils/debug';

export enum StepKey {
  VideoIntro = 'video-intro',
  AccountDetails = 'account-details',
  Calendar = 'calendar',
  ProjectSetup = 'project-setup',
  DataSources = 'data-sources',
}

export const InitialStep = StepKey.VideoIntro;

export enum OnboardingFetchKeys {
  CompanyDetails = 'companyDetails',
  CompanyClassifications = 'companyClassifications',
}

export interface CompanyDetailsBase {
  name: string | null;
  domain: string;
  logo: string;
}

export interface CompanyDetails extends CompanyDetailsBase {
  id: string;
  description: string;
  foundedYear: number;
  domainAliases: Array<string>;
  site: {
    phoneNumbers: Array<string>;
    emailAddresses: Array<string>;
  };
  category: {
    sector: string;
    industryGroup: string;
    industry: string;
    sicCode: string;
    naicsCode: string;
    subIndustry: string;
  };
  tags: Array<string>;
  tech: Array<string>;
  techCategories: Array<string>;
  metrics: {
    alexGlobalRank: number;
    alexUsRank: number;
    employees: number;
    employeesRange: string;
    marketCap: string | null;
    raised: number | null;
    annualRevenue: number | null;
    estimatedAnnualRevenue: string;
    fiscalYearEnd: string | null;
  };
}

export interface OnboardingState {
  step: StepKey;
  xhrStatus: XhrStatus;
  clearbitCompanyDetails: CompanyDetails | null;
  clearbitCompanyClassifications: Array<string>;
}

export const initialState = {
  step: InitialStep,
  xhrStatus: xhrStatusFactory(),
  clearbitCompanyDetails: null,
  clearbitCompanyClassifications: [],
};

const { wrapFetching, withXhrStatusReducer } = createXhrStatusModule('onboarding');
const actionCreator = actionCreatorFactory('timely/onboarding');

const setStepCore = actionCreator<StepKey>('SET_STEP');
export const setClearbitCompanyDetails = actionCreator<CompanyDetails | null>(
  'SET_COMPANY_DETAILS'
);
export const resetClearbitCompanyDetails = actionCreator('RESET_COMPANY_DETAILS');
export const setClassifications = actionCreator<Array<string>>('SET_COMPANY_CLASSIFICATIONS');

const onboardingReducer = reducerWithInitialState<OnboardingState>(initialState)
  .caseWithAction(setStepCore, (state, action) => {
    const step = action.payload;
    const allowedSteps = Object.values(StepKey);
    if (!allowedSteps.includes(step)) {
      return state;
    }
    return {
      ...state,
      step,
    };
  })
  .caseWithAction(setClearbitCompanyDetails, (state, action) => {
    return {
      ...state,
      clearbitCompanyDetails: action.payload,
    };
  })
  .caseWithAction(resetClearbitCompanyDetails, state => {
    return {
      ...state,
      clearbitCompanyDetails: null,
    };
  })
  .caseWithAction(setClassifications, (state, action) => {
    return {
      ...state,
      clearbitCompanyClassifications: action.payload,
    };
  });

export const getCompanyDetails = (
  companyName: string,
  verifyDomain?: boolean
): Thunk<Promise<CompanyDetails | null>> => {
  return dispatch => {
    return dispatch(
      wrapFetching(
        onboardingApi
          .getCompanyDetails(companyName, verifyDomain)
          .then(response => {
            // If we get an empty response back, treat it as a failure
            if (!Object.keys(response).length) {
              dispatch(setClearbitCompanyDetails(null));
              return null;
            }

            dispatch(setClearbitCompanyDetails(response));
            return response;
          })
          .catch(() => {
            dispatch(setClearbitCompanyDetails(null));
            debug.warn('Company lookup failed');
            return null;
          }),
        OnboardingFetchKeys.CompanyDetails
      )
    );
  };
};

export const getCompanyDetailsFromUserEmail = (): Thunk => {
  return (dispatch, getState) => {
    const { email } = getUser(getState());
    const domain = email.split('@')[1];

    return dispatch(getCompanyDetails(domain, true));
  };
};

export const getCompanyClassifications = (): Thunk => {
  return dispatch => {
    return dispatch(
      wrapFetching(
        onboardingApi.getCompanyClassifications().then(res => {
          dispatch(setClassifications(res));
        }),
        OnboardingFetchKeys.CompanyClassifications
      )
    );
  };
};

export function completeOnboarding(
  onboardingData?: {
    account: Account;
    onboardingDraft: OnboardingFormData | null;
  },
  lastStep: number | null = null
): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    if (lastStep) {
      dispatch(saveUserProperty(MEMORY_TOUR_STEP, lastStep - 1));
      dispatch(saveUserProperty(MEMORY_TOUR_COMPLETED));
    }

    if (getIsTourForced(getState())) {
      dispatch(clearTour());
    }

    // Save onboarding draft data as account properties
    if (onboardingData?.account && onboardingData?.onboardingDraft) {
      const { account, onboardingDraft } = onboardingData;
      const accountProperties = [];

      if (onboardingDraft.peopleSize) {
        accountProperties.push({
          account_id: account.id,
          property: AcceptedAccountOnboardingProperties.CompanySize,
          value: onboardingDraft.peopleSize,
        });
      }

      if (onboardingDraft.useCase) {
        accountProperties.push({
          account_id: account.id,
          property: AcceptedAccountOnboardingProperties.OnboardingUseCase,
          value: onboardingDraft.useCase,
        });
      }

      if (onboardingDraft.industry) {
        accountProperties.push({
          account_id: account.id,
          property: AcceptedAccountOnboardingProperties.Industry,
          value: onboardingDraft.industry,
        });
      }

      if (accountProperties.length > 0) {
        try {
          await dispatch(saveAccountProperties(accountProperties));
        } catch (e) {
          debug.warn('Failed to save onboarding account properties:', e);
        }
      }
    }

    return dispatch(
      saveUser({
        day_view_onboarded: true,
        memory_onboarded: true,
      })
    );
  };
}

export const setStep =
  (step: StepKey): Thunk<Promise<unknown>> =>
  dispatch => {
    dispatch(setStepCore(step));
    return dispatch(saveUserProperty(LAST_SEEN_ONBOARDING_STEP, step));
  };

export default withXhrStatusReducer(onboardingReducer);
